import {Injectable, NgZone} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {firstValueFrom, retry} from "rxjs";
import {EndRecordingRequest, PutAudioRequest, PutAudioResponse} from "scribe-poc-shared";
import {environment} from "../../environments/environment";
import {TranscriptionState} from "@allaihealth/util";
import {AppointmentService} from "./appointment.service";
import {AppointmentsService} from "./appointments.service";
import {SnackbarService} from "./snackbar.service";
import {Capacitor} from "@capacitor/core";
import {SessionService} from "./session.service";
import {Uploader, UploadEvent} from "@capgo/capacitor-uploader";
import {RecordingToUpload} from "../lib/recording-to-upload";
import {MatDialog} from "@angular/material/dialog";
import {YesNoData, YesNoDialogComponent} from "../components/yes-no-dialog/yes-no-dialog.component";

/** Service for making asynchronous uploads */
@Injectable({
	providedIn: 'root'
})
export class UploadService {
	/** Current async uploads being made, keyed by their upload id */
	private readonly uploads = new Map<string, RecordingToUpload[]>();

	/** Recordings that are being actively uploaded */
	private readonly inProgress = new Map<string, RecordingToUpload>;

	/** Is user messaging currently being shown? */
	private _isShown:boolean = false;

	constructor(private http:HttpClient,
	            private ngZone:NgZone,
	            private dialog:MatDialog,
	            private snackbar:SnackbarService,
	            private sessionService:SessionService,
	            private appointmentsService:AppointmentsService,
	            private appointmentService:AppointmentService) {
		Uploader.addListener("events", e => this.onProgressEvent(e));
	}

	/**
	 * Start the upload of an audio part
	 * @param data the request data
	 * @param result the recording result to upload
	 */
	putAudioPart(data:PutAudioRequest, result:RecordingToUpload) {
		this.inProgress.set(result.id, result);

		//make sure promises list is initialized
		let uploads = this.uploads.get(data.appointmentId);
		if(!uploads) {
			uploads = [];
			this.uploads.set(data.appointmentId, uploads);
		}
		uploads.push(result);

		//create a new progress object and track
		this.doUpload(data, result).catch((e) => {
			console.error(`Failed to upload recording ${data.appointmentId}`, e);
			this.snackbar.error(`Failed to upload recording for ${this.getErrorContext(data.appointmentId)}`);

			this.inProgress.delete(result.id);
		});

		this.updateUserMessaging();
	}

	/**
	 * Do the actual uploading asynchronously
	 * @param data the request data
	 * @param upload the recording result to upload
	 */
	private async doUpload(data:PutAudioRequest, upload:RecordingToUpload):Promise<void> {
		console.info(`Starting upload ${data.appointmentId}:${data.type}:${data.category}...`);

		let response:PutAudioResponse;

		try {
			//initiate the upload and get a signed url for the upload
			response = await firstValueFrom(
				this.http.put<PutAudioResponse>(`${environment.apiUrl}/recording/add`, data)
					.pipe(retry({delay: 5000, count: 3})));
		} catch(e) {
			console.error(`Failed to do upload asking the user if they want to retry`, e);

			if(await this.doRetry(data.appointmentId)) {
				//retry
				return this.doUpload(data, upload);
			} else {
				//finish the upload with an error
				upload.complete(false);
				this.inProgress.delete(upload.id);
				this.updateUserMessaging();
				return;
			}
		}

		//update the recording in the service
		this.appointmentService.addNewRecording(response.recording);

		await upload.upload(response.signedUploadUrl, data);

		//replace the temporary id with the upload id
		this.inProgress.delete(upload.id);
		this.inProgress.set(upload.uploadId, upload);

		this.updateUserMessaging();
	}

	/**
	 * Called from background upload to notify of an update in the uploads
	 * @param e the update event
	 */
	private async onProgressEvent(e:UploadEvent) {
		const upload = this.inProgress.get(e.id);

		switch(e.name) {
			case "uploading":
				upload.percent = e.payload.percent / 100;
				break;
			case "completed":
				upload.complete();
				this.inProgress.delete(upload.uploadId);
				break;
			case "failed":
				console.error(`Failed to upload recording ${upload.appointmentId}:${upload.uploadId}`, e);
				this.inProgress.delete(upload.uploadId);

				if(await this.doRetry(upload.appointmentId)) {
					//retry
					await upload.retry();
					this.inProgress.set(upload.uploadId, upload);
				} else {
					//finish the upload with an error
					upload.complete(false);
					this.updateUserMessaging();
				}

				break;
		}

		this.ngZone.run(() => {
			this.updateUserMessaging();
		});
	}

	/**
	 * Ask the user if they want to retry the recording upload
	 * @param appointmentId the appointment ID for context to the user
	 */
	private async doRetry(appointmentId:string):Promise<boolean> {
		const patient = this.getErrorContext(appointmentId);

		//ask the user if they want to retry
		const retry = await firstValueFrom(this.dialog.open<YesNoDialogComponent, YesNoData, boolean>(
			YesNoDialogComponent, {
				data: {
					yes: "Retry",
					no: "No",
					title: "Upload Interrupted",
					message: `We couldn’t upload the recording for "${patient}" due to a connection issue. Please check your connection or wait a moment, then tap Retry.`,
					disableClose: false
				}
			})
			.afterClosed());

		if(retry) {
			console.info(`User decided to retry uploading ${appointmentId}`);
			return true;
		}

		//ask the user if they want to discard
		const areYouSure = await firstValueFrom(this.dialog.open<YesNoDialogComponent, YesNoData, boolean>(
			YesNoDialogComponent, {
				data: {
					yes: "Discard",
					no: "Cancel",
					title: "Discard recording?",
					message: ` If you cancel the upload, the recording for "${patient}" will be lost.`,
					disableClose: false
				}
			})
			.afterClosed());

		if(areYouSure) {
			console.info(`User decided to discard recording for ${appointmentId}`);
			return false;
		}

		return this.doRetry(appointmentId);
	}

	/**
	 * End a recording, this will wait for previous parts to finish and then send the end
	 * @param appointmentId the appointment the recordings are for
	 * @param templateId the template that the provider chose for ending
	 */
	async endRecording(appointmentId:string, templateId:string) {
		try {
			//get the current recording (if we're still on the page, or it's in the appointments list
			const recording = this.appointmentService.current.recording?.appointmentId == appointmentId ?
				this.appointmentService.current.recording : this.appointmentsService.getRecording(appointmentId);

			//if found notify services, otherwise we just ignore this part
			if(recording) {
				recording.state = TranscriptionState.Transcription;
				recording.templateId = templateId;
				recording.modified = new Date().getTime();
				this.appointmentService.addNewRecording(recording);
			}

			console.info(`Queuing end of recording for ${appointmentId}`);

			const promises = (this.uploads.get(appointmentId) ?? [])
				.filter(item => !item.isDone)
				.map(item => item.onComplete)

			//wait for previous promises
			if(promises.length == 0) {
				console.info("Promises array was empty, continuing...");
			} else {
				console.info(`Waiting for ${promises.length} promises`);
				await Promise.all(promises);
			}

			console.info(`Ending recording for ${appointmentId}`);

			//send the end to the server
			await firstValueFrom(this.http.post<PutAudioResponse>(
				`${environment.apiUrl}/recording/end/${appointmentId}`, {templateId} as EndRecordingRequest));

			this.uploads.delete(appointmentId);
		} catch(e) {
			console.error(`Failed to end recording ${appointmentId}`, e);
			this.snackbar.error(`There was an error ending recording for patient ${this.getErrorContext(appointmentId)}.`);
		}

		this.updateUserMessaging();
	}

	/** Update the user messaging after changes */
	private updateUserMessaging() {
		try {
			//go through each upload and see if it is done
			let shouldShow = this.inProgress.size > 0;

			//if the messaging should be changed
			if(shouldShow != this._isShown) {
				if(shouldShow) {
					this.enableUserMessaging();
				} else {
					this.disableUserMessaging();
				}

				this._isShown = shouldShow;
			}

		} catch(e) {
			console.error("Failed to update user messaging for uploads", e);
		}
	}

	/** Show warnings to the user if necessary */
	private enableUserMessaging() {
		//browser only warnings
		if(!Capacitor.isNativePlatform()) {
			window.onbeforeunload = (e) => {
				e.preventDefault();
				return true;
			}
		}

		// only should the background recording once
		const notify = localStorage.getItem(this.storageKey);
		if(notify != "true") {
			this.snackbar.message("Recording uploading in the background. Track its progress with the loading bar at the top of the page.", "Ok");
			localStorage.setItem(this.storageKey, "true");
		}
	}

	/** Hide user warnings */
	private disableUserMessaging() {
		//remove browser only warnings
		if(!Capacitor.isNativePlatform()) {
			window.onbeforeunload = undefined
		}
	}

	/** Return a patient's name if they can be found, otherwise return the appointment ID */
	private getErrorContext(appointmentId:string) {
		try {
			const appointment = this.appointmentsService.getRecording(appointmentId);
			const patient = this.appointmentsService.getPatient(appointment.patientId);
			return `${patient.first_name} ${patient.last_name}`
		} catch(e) {
			console.warn(`Failed to find patient context for ${appointmentId}`);
			return appointmentId;
		}
	}

	/** Is user messaging currently being shown? */
	get isShown() {
		return this._isShown;
	}

	/** Total percentage of uploads being uploaded out of 1 */
	get totalProgress() {
		let count = 0;
		let total = 0;

		for(const upload of this.uploads.values()) {
			for(const progress of upload) {
				total += progress.percent
				count++;
			}
		}

		return total / count;
	}

	/** Storage key for {@link localStorage} */
	private get storageKey() {
		return `${this.sessionService.allaiUser.id}-upload-notify`;
	}
}
